import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutWrapper from '../components/LayoutWrapper';
import {
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  EnvelopeIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import config from '../config';
import Helper from '../helpers';

const baseUrl = config.apiBaseUrl;

const UsersAtRiskReport = () => {
  const navigate = useNavigate();
  const [venueKey, setVenueKey] = useState('');
  const [daysThreshold, setDaysThreshold] = useState(30);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterRiskLevel, setFilterRiskLevel] = useState('All');

  useEffect(() => {
    // Get venue key from token claims
    const getVenueKeyFromToken = async () => {
      const token = await Helper.getCookie('wh-jwt');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const adminVenueClaim = payload.admin_venue;
          if (adminVenueClaim) {
            setVenueKey(adminVenueClaim);
          }
        } catch (error) {
          console.error('Error parsing token:', error);
        }
      }
    };

    getVenueKeyFromToken();
  }, []);

  const fetchUsersAtRiskReport = useCallback(async () => {
    if (!venueKey) {
      setError('Venue key not found');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = await Helper.getCookie('wh-jwt');
      const response = await fetch(
        `${baseUrl}/api/venues/${venueKey}/reports/users-at-risk?daysThreshold=${daysThreshold}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        setError('Failed to fetch users at risk report');
      }
    } catch (err) {
      console.error('Error fetching report:', err);
      setError('Error loading report');
    } finally {
      setLoading(false);
    }
  }, [venueKey, daysThreshold]);

  // Auto-fetch report when venue key is available
  useEffect(() => {
    if (venueKey) {
      fetchUsersAtRiskReport();
    }
  }, [venueKey, fetchUsersAtRiskReport]);

  const getRiskBadgeColor = (riskLevel) => {
    switch (riskLevel) {
      case 'High':
        return 'bg-red-500';
      case 'Medium':
        return 'bg-yellow-500';
      case 'Low':
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  };

  const filteredUsers = reportData?.atRiskUsers?.filter(user => 
    filterRiskLevel === 'All' || user.riskLevel === filterRiskLevel
  ) || [];

  const handleContactUser = (email) => {
    window.location.href = `mailto:${email}`;
  };

  return (
    <LayoutWrapper
      title="Users at Risk Report"
      subtitle="Identify users with low attendance to re-engage and retain"
    >
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/reports')}
          className="flex items-center text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Reports
        </button>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-500 text-red-400 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Configuration */}
      <div className="card p-6 mb-6">
        <h3 className="text-lg font-semibold text-white mb-4">Report Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Inactivity Threshold (Days)
            </label>
            <input
              type="number"
              value={daysThreshold}
              onChange={(e) => setDaysThreshold(parseInt(e.target.value, 10) || 30)}
              min="1"
              max="365"
              className="input-field w-full"
            />
            <p className="text-xs text-gray-400 mt-1">
              Users with no bookings for this many days are flagged as high risk
            </p>
          </div>

          <div className="flex items-end">
            <button
              onClick={fetchUsersAtRiskReport}
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? 'Loading...' : 'Refresh Report'}
            </button>
          </div>
        </div>
      </div>

      {/* Report Results */}
      {reportData && (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                    <UserGroupIcon className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="text-sm font-medium text-gray-300">Total Users</h4>
                </div>
              </div>
              <p className="text-3xl font-bold text-white">
                {reportData.totalUsersAnalyzed || 0}
              </p>
            </div>

            <div className="card p-6 border border-red-500/30">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center mr-3">
                    <ExclamationTriangleIcon className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="text-sm font-medium text-gray-300">High Risk</h4>
                </div>
              </div>
              <p className="text-3xl font-bold text-red-400">
                {reportData.highRiskCount || 0}
              </p>
            </div>

            <div className="card p-6 border border-yellow-500/30">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center mr-3">
                    <ExclamationTriangleIcon className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="text-sm font-medium text-gray-300">Medium Risk</h4>
                </div>
              </div>
              <p className="text-3xl font-bold text-yellow-400">
                {reportData.mediumRiskCount || 0}
              </p>
            </div>

            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gray-600 rounded-lg flex items-center justify-center mr-3">
                    <CalendarDaysIcon className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="text-sm font-medium text-gray-300">Low Risk</h4>
                </div>
              </div>
              <p className="text-3xl font-bold text-gray-400">
                {reportData.lowRiskCount || 0}
              </p>
            </div>
          </div>

          {/* Filter Controls */}
          <div className="card p-4 mb-6">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-300">Filter by Risk Level:</span>
              <div className="flex gap-2">
                {['All', 'High', 'Medium', 'Low'].map(level => (
                  <button
                    key={level}
                    onClick={() => setFilterRiskLevel(level)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      filterRiskLevel === level
                        ? 'bg-primary-500 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {level}
                    {level !== 'All' && ` (${reportData[`${level.toLowerCase()}RiskCount`] || 0})`}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-white mb-4">
              At-Risk Users ({filteredUsers.length})
            </h3>
            {filteredUsers.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Risk Level
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Reason
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Last Booking
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Total Sessions
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Recent Activity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {filteredUsers.map((user, index) => (
                      <tr key={index} className="hover:bg-gray-800/50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-white">
                              {user.userName}
                            </span>
                            <span className="text-xs text-gray-400">
                              {user.userEmail}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${getRiskBadgeColor(user.riskLevel)}`}>
                            {user.riskLevel}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-300">
                          {user.riskReason}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {user.lastBookedDate 
                            ? new Date(user.lastBookedDate).toLocaleDateString()
                            : 'Never'
                          }
                          {user.lastBookedDate && (
                            <div className="text-xs text-gray-500">
                              {user.daysSinceLastBooking} days ago
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {user.totalSessionsBooked}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          <div className="flex flex-col">
                            <span>Last month: {user.sessionsInLastMonth}</span>
                            <span className="text-xs text-gray-500">
                              Previous: {user.sessionsInPreviousMonth}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            onClick={() => handleContactUser(user.userEmail)}
                            className="inline-flex items-center px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
                            title={`Email ${user.userName}`}
                          >
                            <EnvelopeIcon className="h-4 w-4 mr-1" />
                            Contact
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <UserGroupIcon className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">
                  {filterRiskLevel === 'All' 
                    ? 'No at-risk users found. Great job keeping everyone engaged!'
                    : `No users found with ${filterRiskLevel} risk level.`
                  }
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {!reportData && !loading && (
        <div className="card p-12 text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">Configure settings and generate a report to identify at-risk users</p>
        </div>
      )}
    </LayoutWrapper>
  );
};

export default UsersAtRiskReport;
